import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@chillout/ui'
import { AdminModal, ModalActions } from '../../../shared/Modal'
import { SuccessIcon } from '../../../shared/SuccessIcon'
import { CopyModalActions } from '../../../shared/CopyModalActions'
import { useBranches } from '../useBranches'
import { useRegisterDevice } from './useRegisterDevice'
import { registerDeviceSchema, formatPairingCode } from '../deviceSchemas'
import { useCopyWithFeedback } from '../../useCopyWithFeedback'
import { AddDeviceForm } from './AddDeviceForm'
import { AddDeviceSuccess } from './AddDeviceSuccess'

interface AddDeviceModalProps {
  isVisible: boolean
  onClose: () => void
}

export function AddDeviceModal({ isVisible, onClose }: AddDeviceModalProps) {
  const { t } = useTranslation()
  const { data: branches = [] } = useBranches()
  const branchOptions = branches.map((b) => ({ label: b.name, value: b.id }))
  const mutation = useRegisterDevice()
  const resetMutation = mutation.reset

  const { control, handleSubmit, watch, reset } = useForm({
    resolver: zodResolver(registerDeviceSchema(t)),
    defaultValues: { name: '', branchId: '' },
  })

  const watchedName = watch('name')
  const watchedBranchId = watch('branchId')
  const branchName = branchOptions.find((b) => b.value === watchedBranchId)?.label ?? ''

  useEffect(() => {
    if (isVisible) {
      reset()
      resetMutation()
    }
  }, [isVisible, reset, resetMutation])

  const pairingCode = mutation.data?.pairingCode ?? ''
  const { copied, copy: handleCopyCode } = useCopyWithFeedback(pairingCode)

  const onSubmit = handleSubmit(({ name, branchId }) => {
    mutation.mutate({ name: name.trim(), branchId })
  })

  const successActions = (
    <CopyModalActions
      copied={copied}
      copyLabel={t('operations.addDevice.copyCodeButton')}
      copiedLabel={t('operations.addDevice.copiedCodeButton')}
      doneLabel={t('common.done')}
      onCopy={handleCopyCode}
      onDone={onClose}
    />
  )

  const formActions = (
    <ModalActions>
      <Button label={t('common.cancel')} variant="secondary" className="flex-1" onPress={onClose} />
      <Button
        label={t('operations.addDevice.submitButton')}
        loadingLabel={t('operations.addDevice.submittingButton')}
        variant="primary"
        className="flex-1"
        loading={mutation.isPending}
        onPress={onSubmit}
      />
    </ModalActions>
  )

  return (
    <AdminModal
      visible={isVisible}
      onClose={onClose}
      closeAccessibilityLabel={t('operations.addDevice.closeAccessibilityLabel')}
      title={mutation.isSuccess ? t('operations.addDevice.successTitle') : t('operations.addDevice.title')}
      icon={mutation.isSuccess ? <SuccessIcon /> : undefined}
      actions={mutation.isSuccess ? successActions : formActions}
    >
      {mutation.isSuccess ? (
        <AddDeviceSuccess
          deviceName={watchedName}
          branchName={branchName}
          pairingCode={pairingCode}
          formattedCode={formatPairingCode(pairingCode)}
          expiresAt={mutation.data?.expiresAt ?? null}
          onCopy={handleCopyCode}
        />
      ) : (
        <AddDeviceForm
          control={control}
          branchOptions={branchOptions}
          isError={mutation.isError}
          error={mutation.error}
        />
      )}
    </AdminModal>
  )
}
